import type { SuperTrendResult } from "../../model/calcSuperTrend";
import type { SuperTrendSettings } from "../../model";
import type { ZoomRange, CandleWithFlag } from "features/chart";
import { GRID, CANDLE_MIN_WIDTH } from "features/chart";

export function drawSuperTrend(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  data: CandleWithFlag[],
  zoom: ZoomRange,
  result: SuperTrendResult,
  settings: SuperTrendSettings,
): void {
  const { left, right, top, bottom } = GRID;
  const chartWidth = width - left - right;
  const chartHeight = height - top - bottom;
  const { start, end } = zoom;
  const startIdx = Math.floor(start);
  const endIdx = Math.min(Math.floor(end), data.length - 1);
  const visibleCount = Math.floor(end) - startIdx + 1;
  const cw = Math.max(CANDLE_MIN_WIDTH, chartWidth / visibleCount);

  let minPrice = Infinity, maxPrice = -Infinity;
  for (let i = startIdx; i <= endIdx; i++) {
    const c = data[i]; if (!c) continue;
    minPrice = Math.min(minPrice, c.low);
    maxPrice = Math.max(maxPrice, c.high);
  }
  if (minPrice === Infinity) return;
  const priceRange = maxPrice - minPrice || 1;
  minPrice -= priceRange * 0.05;
  maxPrice += priceRange * 0.05;
  const range = maxPrice - minPrice;
  const priceToY = (p: number) => top + ((maxPrice - p) / range) * chartHeight;
  const getX = (i: number) => left + (i - startIdx + 0.5) * cw;

  ctx.clearRect(0, 0, width, height);

  // Draw colored segments: uptrend = down.color, downtrend = up.color
  let segStart = -1;
  let segIsUp = true;

  const flushSegment = (segEnd: number) => {
    if (segStart === -1) return;
    const line = segIsUp ? settings.down : settings.up;
    if (!line.visible) { segStart = -1; return; }
    ctx.strokeStyle = line.color;
    ctx.lineWidth = line.width;
    ctx.beginPath();
    for (let i = segStart; i <= segEnd; i++) {
      const v = result.values[i]; if (v === null) continue;
      i === segStart ? ctx.moveTo(getX(i), priceToY(v)) : ctx.lineTo(getX(i), priceToY(v));
    }
    ctx.stroke();
    segStart = -1;
  };

  for (let i = startIdx; i <= endIdx; i++) {
    const v = result.values[i];
    if (v === null) { flushSegment(i - 1); continue; }
    const isUp = result.isUptrend[i];
    if (segStart === -1) { segStart = i; segIsUp = isUp; }
    else if (isUp !== segIsUp) { flushSegment(i - 1); segStart = i; segIsUp = isUp; }
  }
  flushSegment(endIdx);
}
